import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Gavel } from 'lucide-react';
import api from '../services/api';

/**
 * Global WebSocket listener for auction snipe alerts.
 * Mounts once in App.js — when a bid triggers anti-snipe on the seller's auction,
 * this shows a real-time toast with bid details and a link to the auction.
 */
export function AuctionSnipeListener() {
  const wsRef = useRef(null);
  const mountedRef = useRef(true);
  const reconnectRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;

    const connect = () => {
      const token = api.getToken();
      if (!token) return;
      if (wsRef.current?.readyState === WebSocket.OPEN) return;

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/api/ws/chat`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({ token }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'auction_snipe_alert') {
            const { title, bid_amount, bidder_name, auction_id, time_added_seconds } = data;
            const mins = Math.round((time_added_seconds || 120) / 60);
            toast(
              <div className="flex items-start gap-3">
                <div className="bg-red-100 rounded-full p-2 flex-shrink-0 mt-0.5">
                  <Gavel className="w-5 h-5 text-red-600" />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-gray-900 text-sm">Last-Minute Bid!</p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    <strong>{bidder_name}</strong> bid <strong className="text-red-600">AU${bid_amount?.toLocaleString()}</strong> on your auction
                  </p>
                  <p className="text-xs text-gray-900 font-semibold mt-0.5 truncate">{title}</p>
                  <p className="text-[11px] text-amber-600 mt-1 font-medium">
                    Anti-snipe: +{mins} min added
                  </p>
                  <a
                    href={`/auction/${auction_id}`}
                    className="inline-block mt-2 text-xs font-bold text-red-600 hover:text-red-700 underline underline-offset-2"
                    data-testid="snipe-alert-link"
                  >
                    View Auction
                  </a>
                </div>
              </div>,
              {
                duration: 12000,
                position: 'top-right',
                className: 'snipe-alert-toast',
              }
            );
          }
        } catch { /* ignore parse errors */ }
      };

      ws.onclose = () => {
        if (mountedRef.current) {
          reconnectRef.current = setTimeout(connect, 5000);
        }
      };

      ws.onerror = () => { ws.close(); };
    };

    // Connect and re-check token periodically (user may log in after mount)
    connect();
    const tokenCheck = setInterval(() => {
      const token = api.getToken();
      if (token && (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED)) {
        connect();
      }
      if (!token && wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    }, 30000);

    // Ping to keep alive
    const ping = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => {
      mountedRef.current = false;
      clearInterval(tokenCheck);
      clearInterval(ping);
      clearTimeout(reconnectRef.current);
      wsRef.current?.close();
    };
  }, []);

  return null; // Invisible component
}
